import SEO from '@/components/common/SEO';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { useDebounce } from '@/hooks/useDebounce';
import { 
  Search, 
  Calendar, 
  Clock, 
  ArrowRight, 
  Grid, 
  List, 
  X, 
  Radio,
  Play,
  Award,
  Layers,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Filter,
  RotateCcw,
  ArrowUpDown,
  CalendarDays,
  CalendarRange,
  Timer,
  Hourglass,
  Package
} from 'lucide-react';
import { webinarsAPI, industriesAPI, speakersAPI, bundlesAPI } from '@/services/api';
import { format, isWithinInterval, isSameDay, isSameMonth, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { formatET, getETAbbr } from '@/utils/dateUtils';
import { WebinarCard } from '@/components/common/WebinarCard';

const ITEMS_PER_PAGE = 20;

// HR Compliance industry check for CPE badge display
const HR_COMPLIANCE_NAMES = ['HR Compliance', 'HR compliance', 'Human Resources Compliance'];

// Helper to check if webinar is HR Compliance industry (for CPE badge display)
const isHRCompliance = (industryName, subIndustryName) => {
  if (!industryName && !subIndustryName) return false;
  const nameToCheck = subIndustryName || industryName || '';
  return HR_COMPLIANCE_NAMES.some(hr => 
    nameToCheck.toLowerCase().includes(hr.toLowerCase())
  );
};

// HRCI Logo URL for CPE badge
const HRCI_LOGO_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api/static/images/hrci-logo.svg`;

// Industry-based fallback images (Corporate Training Style - Generated per industry)
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const industryImages = {
  'Cross Industry Functions': `${BASE_URL}/api/static/images/industries/cross_industry_functions_1dc8cb34.png`,
  'Healthcare and Hospital': `${BASE_URL}/api/static/images/industries/healthcare_and_hospital_40b680e4.png`,
  'Healthcare': `${BASE_URL}/api/static/images/industries/healthcare_and_hospital_40b680e4.png`,
  'Banking Financial Services and Insurance': `${BASE_URL}/api/static/images/industries/banking_financial_services_and_insurance_5dc7a75a.png`,
  'Manufacturing': `${BASE_URL}/api/static/images/industries/manufacturing_4fbaa269.png`,
  'Construction': `${BASE_URL}/api/static/images/industries/construction_55779e1e.png`,
  'Legal and Law': `${BASE_URL}/api/static/images/industries/legal_and_law_e7a3978e.png`,
  'Legal': `${BASE_URL}/api/static/images/industries/legal_and_law_e7a3978e.png`,
  'Life Sciences': `${BASE_URL}/api/static/images/industries/life_sciences_e1dad885.png`,
  'Banks and Credit Unions': `${BASE_URL}/api/static/images/industries/banks_and_credit_unions_325709aa.png`,
  'Banking': `${BASE_URL}/api/static/images/industries/banks_and_credit_unions_325709aa.png`,
  'Financial Services and Trading': `${BASE_URL}/api/static/images/industries/financial_services_and_trading_89ceb2d8.png`,
  'Financial': `${BASE_URL}/api/static/images/industries/financial_services_and_trading_89ceb2d8.png`,
  'Insurance': `${BASE_URL}/api/static/images/industries/insurance_e60007e4.png`,
  'Medical Devices': `${BASE_URL}/api/static/images/industries/medical_devices_179555d1.png`,
  'Drugs & Chemicals': `${BASE_URL}/api/static/images/industries/drugs_chemicals_1e4925e4.png`,
  'Biotechnology': `${BASE_URL}/api/static/images/industries/biotechnology_c1285ae0.png`,
  'Clinical Trial and Clinical Research': `${BASE_URL}/api/static/images/industries/clinical_trial_and_clinical_research_f072cf4a.png`,
  'Clinical': `${BASE_URL}/api/static/images/industries/clinical_trial_and_clinical_research_f072cf4a.png`,
  'Laboratory': `${BASE_URL}/api/static/images/industries/laboratory_1900608a.png`,
  'Food and Dietary Supplements': `${BASE_URL}/api/static/images/industries/food_and_dietary_supplements_2a4f16b1.png`,
  'All FDA Regulated Industry': `${BASE_URL}/api/static/images/industries/all_fda_regulated_industry_4b39b1e0.png`,
  'FDA': `${BASE_URL}/api/static/images/industries/all_fda_regulated_industry_4b39b1e0.png`,
  'Pharmaceutical': `${BASE_URL}/api/static/images/industries/pharmaceutical_055fbb4f.png`,
  'Quality and Safety': `${BASE_URL}/api/static/images/industries/quality_and_safety_74265bc8.png`,
  'Quality': `${BASE_URL}/api/static/images/industries/quality_and_safety_74265bc8.png`,
  'Standards and Methodology': `${BASE_URL}/api/static/images/industries/standards_and_methodology_6a88d70c.png`,
  'Quality Management': `${BASE_URL}/api/static/images/industries/quality_management_351b80cf.png`,
  'Medicaid and Medicare': `${BASE_URL}/api/static/images/industries/medicaid_and_medicare_a96df4ca.png`,
  'HIPAA / HITECH': `${BASE_URL}/api/static/images/industries/hipaa_hitech_5db49ef2.png`,
  'HIPAA': `${BASE_URL}/api/static/images/industries/hipaa_hitech_5db49ef2.png`,
  'Audits & Data Security': `${BASE_URL}/api/static/images/industries/audits_data_security_50fb4653.png`,
  'Hospital Management': `${BASE_URL}/api/static/images/industries/hospital_management_3ab9dc5e.png`,
  'Corporate Governance': `${BASE_URL}/api/static/images/industries/corporate_governance_ab00e884.png`,
  'IT and PCI Compliance': `${BASE_URL}/api/static/images/industries/it_and_pci_compliance_78427d1a.png`,
  'IT': `${BASE_URL}/api/static/images/industries/it_and_pci_compliance_78427d1a.png`,
  'HR Compliance': `${BASE_URL}/api/static/images/industries/hr_compliance_b55d1eb0.png`,
  'HR': `${BASE_URL}/api/static/images/industries/hr_compliance_b55d1eb0.png`,
  'OSHA Compliance': `${BASE_URL}/api/static/images/industries/osha_compliance_59ebc134.png`,
  'OSHA': `${BASE_URL}/api/static/images/industries/osha_compliance_59ebc134.png`,
  'EHS Compliance': `${BASE_URL}/api/static/images/industries/ehs_compliance_5156d634.png`,
  'Trade and Logistics': `${BASE_URL}/api/static/images/industries/trade_and_logistics_747e3358.png`,
  'Accounting and Taxation': `${BASE_URL}/api/static/images/industries/accounting_and_taxation_303fa4d6.png`,
  'Accounting': `${BASE_URL}/api/static/images/industries/accounting_and_taxation_303fa4d6.png`,
  'Payroll Benefits and Compensation': `${BASE_URL}/api/static/images/industries/payroll_benefits_and_compensation_a99ed275.png`,
  'Payroll': `${BASE_URL}/api/static/images/industries/payroll_benefits_and_compensation_a99ed275.png`,
  'Excel Training': `${BASE_URL}/api/static/images/industries/excel_training_36a022f1.png`,
  'Excel': `${BASE_URL}/api/static/images/industries/excel_training_36a022f1.png`,
  'Outlook Training': `${BASE_URL}/api/static/images/industries/outlook_training_c2a7a416.png`,
  'Outlook': `${BASE_URL}/api/static/images/industries/outlook_training_c2a7a416.png`,
  'Sales And Customer Service Coaching': `${BASE_URL}/api/static/images/industries/sales_and_customer_service_coaching_bdd251e3.png`,
  'Sales': `${BASE_URL}/api/static/images/industries/sales_and_customer_service_coaching_bdd251e3.png`,
  'Project Management': `${BASE_URL}/api/static/images/industries/project_management_29eb7e60.png`,
  'Risk Management': `${BASE_URL}/api/static/images/industries/risk_management_68769a2e.png`,
  'Communication and Business Writing': `${BASE_URL}/api/static/images/industries/communication_and_business_writing_0917d5ed.png`,
  'Communication': `${BASE_URL}/api/static/images/industries/communication_and_business_writing_0917d5ed.png`,
  'Public Housing & Compliance': `${BASE_URL}/api/static/images/industries/public_housing_compliance_3c8da890.png`,
  'Law Reform': `${BASE_URL}/api/static/images/industries/law_reform_ce6ff262.png`,
  'Law Enforcement': `${BASE_URL}/api/static/images/industries/law_enforcement_514a4fb6.png`,
  'Generic Webinars': `${BASE_URL}/api/static/images/industries/generic_webinars_4eb618cf.png`,
  'default': `${BASE_URL}/api/static/images/industries/generic_webinars_4eb618cf.png`
};

// Get appropriate thumbnail for webinar based on industry
const getWebinarThumbnail = (thumbnailUrl, industryName) => {
  // Check if thumbnail URL is valid (not empty, not pointing to old preview domain)
  if (thumbnailUrl && !thumbnailUrl.includes('preview.emergentagent.com') && !thumbnailUrl.includes('undefined')) {
    return thumbnailUrl;
  }
  
  // Find exact match first
  if (industryName && industryImages[industryName]) {
    return industryImages[industryName];
  }
  
  // Find partial match
  if (industryName) {
    for (const [key, url] of Object.entries(industryImages)) {
      if (industryName.toLowerCase().includes(key.toLowerCase()) || 
          key.toLowerCase().includes(industryName.toLowerCase())) {
        return url;
      }
    }
  }
  
  return industryImages.default;
};

// Webinar type filter options
const WEBINAR_TYPES = [
  { id: 'live', label: 'Live Webinars', icon: Radio, color: 'text-red-500 bg-red-50' },
  { id: 'recorded', label: 'Recorded Webinars', icon: Play, color: 'text-blue-500 bg-blue-50' },
  { id: 'certified', label: 'Certified Webinars', icon: Award, color: 'text-green-500 bg-green-50' },
  { id: 'series', label: 'Webinar Series', icon: Layers, color: 'text-purple-500 bg-purple-50' },
  { id: 'bestseller', label: 'Best Sellers', icon: TrendingUp, color: 'text-gold-500 bg-gold-50' },
];

// Sort options
const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest First' },
  { id: 'oldest', label: 'Oldest First' },
  { id: 'price_low', label: 'Price: Low to High' },
  { id: 'price_high', label: 'Price: High to Low' },
  { id: 'cpe_high', label: 'CPE Credits: High to Low' },
  { id: 'title_az', label: 'Title: A-Z' },
];

// Date filter modes
const DATE_FILTER_MODES = [
  { id: 'none', label: 'All Dates' },
  { id: 'specific', label: 'Specific Date' },
  { id: 'month', label: 'Specific Month' },
  { id: 'range', label: 'Date Range' },
];

// Month options for dropdown
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function WebinarsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [webinars, setWebinars] = useState([]);
  const [filteredWebinars, setFilteredWebinars] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  
  // Counts from server
  const [counts, setCounts] = useState({ total: 0, live: 0, recorded: 0, certified: 0 });
  
  // Pagination - server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedIndustries, setSelectedIndustries] = useState(() => {
    const industry = searchParams.get('industry');
    return industry ? [industry] : [];
  });
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const type = searchParams.get('type');
    return type ? [type] : [];
  });
  const [activeCategory, setActiveCategory] = useState(() => {
    const type = searchParams.get('type');
    if (type === 'live') return 'live';
    if (type === 'recorded') return 'recorded';
    if (type === 'certified') return 'certified';
    return 'all';
  });
  const [bundles, setBundles] = useState([]);
  const [bundlesLoading, setBundlesLoading] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState('all');
  
  // Sort
  const [sortBy, setSortBy] = useState('newest');
  
  // Date filters
  const [dateFilterMode, setDateFilterMode] = useState('none');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  // Handle URL query params on mount and when they change
  useEffect(() => {
    const type = searchParams.get('type');
    if (type) {
      setSelectedTypes([type]);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchIndustries();
    fetchSpeakers();
  }, []);

  // Fetch webinars and counts when filters change (debounced search to avoid spamming on every keystroke)
  useEffect(() => {
    fetchWebinars();
    fetchCounts();
  }, [selectedIndustries, selectedTypes, selectedSpeaker, debouncedSearch, currentPage, sortBy, dateFilterMode, selectedDate, selectedMonth, selectedYear, dateRange]);

  const fetchIndustries = async () => {
    try {
      // Fetch industries with tree structure (parent/child with └ prefix)
      const response = await industriesAPI.getFlat();
      setIndustries(response.data || []);
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const fetchSpeakers = async () => {
    try {
      const response = await speakersAPI.getAll({ limit: 500 });
      setSpeakers(response.data || []);
    } catch (error) {
      console.error('Error fetching speakers:', error);
    }
  };

  const fetchCounts = async () => {
    try {
      // Build params - only pass industry and search for counts
      // Live/Recorded/Certified counts should always show actual totals
      const params = {};
      
      if (selectedIndustries.length > 0) {
        params.industry_id = selectedIndustries[0];
      }
      
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }
      
      const response = await webinarsAPI.getCounts(params);
      setCounts(response.data || { total: 0, live: 0, recorded: 0, certified: 0 });
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const fetchWebinars = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      // Build params for server-side filtering
      const params = {
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
        sort_by: sortBy,
      };
      
      // Add industry filter (use first selected)
      if (selectedIndustries.length > 0) {
        params.industry_id = selectedIndustries[0];
      }
      
      // Add speaker filter
      if (selectedSpeaker !== 'all') {
        params.speaker_id = selectedSpeaker;
      }
      
      // Add type filter - support multiple types with OR logic
      const typeFilters = [];
      if (selectedTypes.includes('live')) {
        typeFilters.push('live');
      }
      if (selectedTypes.includes('recorded')) {
        typeFilters.push('on_demand');
      }
      if (typeFilters.length > 0) {
        params.webinar_type = typeFilters.join(',');
      }
      
      // Add CPE filter (Certified)
      if (selectedTypes.includes('certified')) {
        params.has_cpe = true;
      }
      
      // Add Series filter
      if (selectedTypes.includes('series')) {
        params.is_series = true;
      }
      
      // Add Bestseller filter
      if (selectedTypes.includes('bestseller')) {
        params.is_bestseller = true;
      }
      
      // Add search
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      // Add date filter (server-side, works across all pages)
      if (dateFilterMode === 'specific' && selectedDate) {
        const d = format(selectedDate, 'yyyy-MM-dd');
        params.date_from = d;
        params.date_to = d;
      } else if (dateFilterMode === 'month') {
        params.month = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
      } else if (dateFilterMode === 'range' && dateRange?.from && dateRange?.to) {
        params.date_from = format(dateRange.from, 'yyyy-MM-dd');
        params.date_to = format(dateRange.to, 'yyyy-MM-dd');
      }

      const response = await webinarsAPI.getAll(params);
      const data = response.data;
      
      // Handle paginated response
      if (data.items) {
        setWebinars(data.items);
        setFilteredWebinars(data.items);
        setTotalCount(data.total);
        setTotalPages(data.total_pages);
      } else {
        // Fallback for array response
        setWebinars(data);
        setFilteredWebinars(data);
        setTotalCount(data.length);
        setTotalPages(Math.ceil(data.length / ITEMS_PER_PAGE));
      }
    } catch (error) {
      console.error('Error fetching webinars:', error);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  // Date filtering is now handled server-side; keep filteredWebinars in sync with fetched data
  useEffect(() => {
    setFilteredWebinars(webinars);
  }, [webinars]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const toggleIndustry = (industryId) => {
    setSelectedIndustries(prev => 
      prev.includes(industryId) 
        ? prev.filter(id => id !== industryId)
        : [...prev, industryId]
    );
    setCurrentPage(1);
  };

  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
    setCurrentPage(1);
  };

  const fetchBundles = async () => {
    setBundlesLoading(true);
    try {
      const res = await bundlesAPI.list();
      setBundles(res.data?.bundles || res.data?.items || []);
    } catch (error) {
      console.error('Error fetching bundles:', error);
    } finally {
      setBundlesLoading(false);
    }
  };

  const selectCategory = (cat) => {
    setActiveCategory(cat);
    setCurrentPage(1);
    if (cat === 'bundles') {
      if (bundles.length === 0) fetchBundles();
      return;
    }
    if (cat === 'all') {
      setSelectedTypes([]);
      return;
    }
    setSelectedTypes([cat]);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedIndustries([]);
    setSelectedTypes([]);
    setSelectedSpeaker('all');
    setDateFilterMode('none');
    setSelectedDate(null);
    setDateRange({ from: null, to: null });
    setCurrentPage(1);
    setSearchParams({});
  };

  const clearDateFilter = () => {
    setDateFilterMode('none');
    setSelectedDate(null);
    setDateRange({ from: null, to: null });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return formatET(dateStr, 'MMM dd, yyyy');
  };

  const formatDayDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return `${format(date, 'EEE')}, ${format(date, 'MMM dd, yyyy')}`;
  };

  const getDateFilterLabel = () => {
    if (dateFilterMode === 'specific' && selectedDate) {
      return format(selectedDate, 'MMM dd, yyyy');
    }
    if (dateFilterMode === 'month') {
      return `${MONTHS[selectedMonth]} ${selectedYear}`;
    }
    if (dateFilterMode === 'range' && dateRange.from && dateRange.to) {
      return `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd, yyyy')}`;
    }
    return null;
  };

  const hasActiveFilters = search || selectedIndustries.length > 0 || selectedTypes.length > 0 || dateFilterMode !== 'none';
  const activeFilterCount = (search ? 1 : 0) + selectedIndustries.length + selectedTypes.length + (dateFilterMode !== 'none' ? 1 : 0);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-12" data-testid="pagination">
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="border-zinc-300"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </Button>

        <div className="flex items-center gap-1">
          {startPage > 1 && (
            <>
              <Button
                variant={currentPage === 1 ? 'default' : 'ghost'}
                size="sm"
                onClick={() => goToPage(1)}
                className={currentPage === 1 ? 'bg-zinc-950' : ''}
              >
                1
              </Button>
              {startPage > 2 && <span className="px-2 text-zinc-400">...</span>}
            </>
          )}

          {pages.map(page => (
            <Button
              key={page}
              variant={currentPage === page ? 'default' : 'ghost'}
              size="sm"
              onClick={() => goToPage(page)}
              className={currentPage === page ? 'bg-zinc-950' : ''}
            >
              {page}
            </Button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="px-2 text-zinc-400">...</span>}
              <Button
                variant={currentPage === totalPages ? 'default' : 'ghost'}
                size="sm"
                onClick={() => goToPage(totalPages)}
                className={currentPage === totalPages ? 'bg-zinc-950' : ''}
              >
                {totalPages}
              </Button>
            </>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="border-zinc-300"
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear + i);

  return (
    <div className="min-h-screen bg-transparent" data-testid="webinars-page">
      <SEO
        title="All Webinars — Live & On-Demand GRC Training"
        description="Browse thousands of expert-led governance, risk & compliance webinars. Filter by industry, date, live or on-demand, and CPE/CE certification. New sessions added weekly."
        path="/webinars"
      />
      {/* Hero Section — full grid canvas (header + search + categories + filters) */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-8">
        <div className="container-custom">
          <Breadcrumb />
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-x-4 gap-y-2 mb-7">
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-[-0.03em] leading-none">
              <span className="text-zinc-950">Webinars </span>
              <span className="text-zinc-400">catalog.</span>
            </h1>
            <p className="text-zinc-500 text-sm md:text-base sm:pb-0.5">
              Expert-led governance, risk &amp; compliance training across every regulated industry.
            </p>
          </div>

          {/* Search + category buttons — one line */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
            <form onSubmit={handleSearch} className="flex-1 w-full">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  type="text"
                  placeholder="Search webinar title, code or speaker..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  className="pl-10 pr-4 h-11 text-sm bg-white border-zinc-300 rounded-lg"
                  data-testid="search-input"
                />
              </div>
            </form>

            {/* Category buttons */}
            <div className="flex flex-wrap gap-2" data-testid="category-buttons">
              {[
                { id: 'all', label: 'All Webinars', icon: Layers, count: counts.total },
                { id: 'live', label: 'Live Webinars', icon: Radio, count: counts.live },
                { id: 'recorded', label: 'Recorded', icon: Play, count: counts.recorded },
                { id: 'certified', label: 'Certified (CPE)', icon: Award, count: counts.certified },
                { id: 'bundles', label: 'Webinar Bundles', icon: Package, count: null },
              ].map((cat) => {
                const Icon = cat.icon;
                const active = activeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => selectCategory(cat.id)}
                    data-testid={`category-${cat.id}`}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                      active
                        ? 'bg-zinc-950 text-white border-zinc-950'
                        : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.label}
                    {cat.count != null && (
                      <span className={`mono-label text-[10px] ${active ? 'text-zinc-300' : 'text-zinc-400'}`}>
                        {cat.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

            {/* Secondary filters row */}
            <div className={`flex flex-wrap items-center gap-3 ${activeCategory === 'bundles' ? 'hidden' : ''}`} data-testid="filter-container">
              {/* Webinar Count */}
              <span className="text-sm font-medium text-zinc-900 whitespace-nowrap">
                {totalCount} Webinars
              </span>
              
              <div className="h-6 w-px bg-zinc-300 hidden sm:block" />
              
              {/* Industries Filter */}
              <SearchableSelect
                options={[
                  { value: 'all', label: 'All Industries' },
                  ...industries.map((ind) => ({ value: ind.id, label: ind.name }))
                ]}
                value={selectedIndustries.length === 1 ? selectedIndustries[0] : 'all'}
                onValueChange={(v) => {
                  if (v === 'all' || !v) {
                    setSelectedIndustries([]);
                  } else {
                    setSelectedIndustries([v]);
                  }
                  setCurrentPage(1);
                }}
                placeholder="All Industries"
                searchPlaceholder="Search industries..."
                className="w-48 border-zinc-300"
                icon={<Filter className="w-3.5 h-3.5 shrink-0 text-zinc-400" />}
                dataTestId="industry-filter"
                preserveOrder={true}
              />
              
              {/* Speakers Filter */}
              <SearchableSelect
                options={[
                  { value: 'all', label: 'All Speakers' },
                  ...speakers.map((sp) => ({ value: sp.id, label: `${sp.first_name} ${sp.last_name}` }))
                ]}
                value={selectedSpeaker}
                onValueChange={(v) => {
                  setSelectedSpeaker(v || 'all');
                  setCurrentPage(1);
                }}
                placeholder="All Speakers"
                searchPlaceholder="Search speakers..."
                className="w-48 border-zinc-300"
                icon={<Filter className="w-3.5 h-3.5 shrink-0 text-zinc-400" />}
                dataTestId="speaker-filter"
              />
              
              {/* Date Filter with Calendar Popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-40 h-9 text-sm border-zinc-300 justify-start font-normal"
                    data-testid="date-filter"
                  >
                    <CalendarDays className="w-3.5 h-3.5 mr-2 text-zinc-400" />
                    {dateFilterMode === 'none' && 'All Dates'}
                    {dateFilterMode === 'specific' && selectedDate && format(selectedDate, 'MMM dd, yyyy')}
                    {dateFilterMode === 'specific' && !selectedDate && 'Select Date'}
                    {dateFilterMode === 'month' && `${MONTHS[selectedMonth]} ${selectedYear}`}
                    {dateFilterMode === 'range' && dateRange.from && dateRange.to && 
                      `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd')}`}
                    {dateFilterMode === 'range' && (!dateRange.from || !dateRange.to) && 'Select Range'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                  {/* Date Filter Mode Tabs */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                      onClick={() => { setDateFilterMode('none'); setSelectedDate(null); setDateRange({ from: null, to: null }); setCurrentPage(1); }}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                        dateFilterMode === 'none' ? 'border-zinc-900 bg-zinc-50 text-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                    >
                      All Dates
                    </button>
                    <button
                      onClick={() => { setDateFilterMode('specific'); setCurrentPage(1); }}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                        dateFilterMode === 'specific' ? 'border-zinc-900 bg-zinc-50 text-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                    >
                      Specific Date
                    </button>
                    <button
                      onClick={() => { setDateFilterMode('month'); setCurrentPage(1); }}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                        dateFilterMode === 'month' ? 'border-zinc-900 bg-zinc-50 text-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                    >
                      By Month
                    </button>
                    <button
                      onClick={() => { setDateFilterMode('range'); setCurrentPage(1); }}
                      className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                        dateFilterMode === 'range' ? 'border-zinc-900 bg-zinc-50 text-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                    >
                      Date Range
                    </button>
                  </div>

                  {/* Specific Date Calendar */}
                  {dateFilterMode === 'specific' && (
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => { setSelectedDate(d); setCurrentPage(1); }}
                      className="rounded-md border border-zinc-200"
                    />
                  )}

                  {/* Month/Year Selector */}
                  {dateFilterMode === 'month' && (
                    <div className="space-y-3">
                      <Select value={selectedMonth.toString()} onValueChange={(v) => { setSelectedMonth(parseInt(v)); setCurrentPage(1); }}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((month, idx) => (
                            <SelectItem key={idx} value={idx.toString()}>{month}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={selectedYear.toString()} onValueChange={(v) => { setSelectedYear(parseInt(v)); setCurrentPage(1); }}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {yearOptions.map((year) => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Date Range Calendar */}
                  {dateFilterMode === 'range' && (
                    <div>
                      <CalendarComponent
                        mode="range"
                        selected={dateRange}
                        onSelect={(r) => { setDateRange(r || { from: null, to: null }); setCurrentPage(1); }}
                        numberOfMonths={1}
                        className="rounded-md border border-zinc-200"
                      />
                      {dateRange.from && dateRange.to && (
                        <p className="text-xs text-zinc-500 mt-2 text-center">
                          {format(dateRange.from, 'MMM dd, yyyy')} - {format(dateRange.to, 'MMM dd, yyyy')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Clear Button */}
                  {dateFilterMode !== 'none' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setDateFilterMode('none'); setSelectedDate(null); setDateRange({ from: null, to: null }); setCurrentPage(1); }}
                      className="w-full mt-3 text-zinc-600"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Clear Date Filter
                    </Button>
                  )}
                </PopoverContent>
              </Popover>
              
              {/* Spacer */}
              <div className="flex-1" />

              {/* Sort */}
              <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-44 h-9 text-sm border-zinc-300" data-testid="sort-filter">
                  <ArrowUpDown className="w-3.5 h-3.5 mr-2 text-zinc-400" />
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Active Filters Pills - Show when filters are active */}
            {activeCategory !== 'bundles' && hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-zinc-100">
                <span className="text-xs text-zinc-500">Active:</span>
                {search && (
                  <Badge 
                    className="bg-zinc-100 text-zinc-700 hover:bg-zinc-200 cursor-pointer text-xs px-2 py-0.5"
                    onClick={() => setSearch('')}
                  >
                    "{search}" <X className="w-3 h-3 ml-1" />
                  </Badge>
                )}
                {selectedTypes.map(typeId => {
                  const type = WEBINAR_TYPES.find(t => t.id === typeId);
                  return (
                    <Badge 
                      key={typeId}
                      className="bg-zinc-900 text-white hover:bg-zinc-800 cursor-pointer text-xs px-2 py-0.5"
                      onClick={() => toggleType(typeId)}
                    >
                      {type?.label} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  );
                })}
                {selectedIndustries.map(industryId => {
                  const industry = industries.find(i => i.id === industryId);
                  return (
                    <Badge 
                      key={industryId}
                      className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 cursor-pointer text-xs px-2 py-0.5"
                      onClick={() => toggleIndustry(industryId)}
                    >
                      {industry?.name} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  );
                })}
                {dateFilterMode !== 'none' && getDateFilterLabel() && (
                  <Badge 
                    className="bg-zinc-100 text-zinc-700 hover:bg-zinc-200 cursor-pointer text-xs px-2 py-0.5"
                    onClick={clearDateFilter}
                  >
                    {getDateFilterLabel()} <X className="w-3 h-3 ml-1" />
                  </Badge>
                )}
                <button
                  onClick={clearFilters}
                  className="text-xs text-zinc-500 hover:text-zinc-950 ml-2"
                >
                  Clear all
                </button>
              </div>
            )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-10 bg-white">
        <div className="container-custom">
            {/* Webinar Results */}
            {activeCategory === 'bundles' ? (
              bundlesLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="animate-pulse bg-white border border-zinc-200 rounded-xl p-6 h-56" />
                  ))}
                </div>
              ) : bundles.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-zinc-200">
                  <Package className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
                  <p className="text-zinc-600">No bundles available right now.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="bundles-grid">
                  {bundles.map((b) => (
                    <Link key={b.id} to={`/bundles/${b.slug}`} className="block group" data-testid={`bundle-card-${b.slug}`}>
                      <div className="h-full bg-white border border-zinc-200 rounded-xl p-6 group-hover:border-zinc-900 transition-colors flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                          <span className="mono-label inline-flex items-center gap-2 text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2.5 py-1">
                            <Package className="w-3 h-3" /> Bundle
                          </span>
                          {b.is_featured && (
                            <Badge className="bg-emerald-100 text-emerald-700 border-none text-[10px]">Featured</Badge>
                          )}
                        </div>
                        {b.category && (
                          <div className="mono-label text-[10px] text-zinc-400 mb-2">{b.category}</div>
                        )}
                        <h3 className="font-heading text-lg font-bold text-zinc-950 leading-snug mb-2 group-hover:text-zinc-700">
                          {b.title}
                        </h3>
                        {b.subtitle && (
                          <p className="text-sm text-zinc-500 line-clamp-3 mb-4 flex-1">{b.subtitle}</p>
                        )}
                        <div className="flex items-center justify-between pt-4 border-t border-zinc-100 mt-auto">
                          {b.price_download != null ? (
                            <span className="font-heading text-xl font-bold text-zinc-950">${b.price_download}</span>
                          ) : <span />}
                          <span className="mono-label inline-flex items-center gap-1 text-[11px] text-zinc-900">
                            View <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )
            ) : loading && filteredWebinars.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="filter-loading-skeleton">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="animate-pulse bg-white border border-zinc-200 rounded-xl p-5">
                    <div className="flex justify-between mb-4">
                      <div className="h-5 w-24 bg-zinc-200 rounded-full" />
                      <div className="h-5 w-16 bg-zinc-200 rounded-full" />
                    </div>
                    <div className="h-4 bg-zinc-200 rounded w-1/3 mb-4" />
                    <div className="h-5 bg-zinc-200 rounded w-3/4 mb-2" />
                    <div className="h-5 bg-zinc-200 rounded w-2/3 mb-5" />
                    <div className="h-4 bg-zinc-200 rounded w-full mb-2" />
                    <div className="h-4 bg-zinc-200 rounded w-1/2 mb-5" />
                    <div className="flex justify-between pt-4 border-t border-zinc-100">
                      <div className="h-6 w-12 bg-zinc-200 rounded" />
                      <div className="h-8 w-24 bg-zinc-200 rounded-md" />
                    </div>
                  </div>
                ))}
              </div>
            ) : fetchError && filteredWebinars.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border border-zinc-200" data-testid="webinars-fetch-error">
                <div className="w-16 h-16 mx-auto bg-zinc-100 rounded-full flex items-center justify-center mb-4">
                  <RotateCcw className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-zinc-900 mb-2">
                  We're experiencing high traffic
                </h3>
                <p className="text-zinc-600 mb-4 max-w-md mx-auto text-sm">
                  The webinar list couldn't be loaded right now. Please try again.
                </p>
                <Button onClick={fetchWebinars} size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="webinars-retry-button">
                  Retry
                </Button>
              </div>
            ) : filteredWebinars.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 mx-auto bg-zinc-100 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-zinc-900 mb-2">
                  No webinars found
                </h3>
                <p className="text-zinc-600 mb-4 max-w-md mx-auto text-sm">
                  Try adjusting your filters or search terms.
                </p>
                <Button onClick={clearFilters} size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white">
                  Clear Filters
                </Button>
              </div>
            ) : (
              /* Grid View - image-free MailCraft cards; dim during background refetch instead of full-page skeleton */
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                {filteredWebinars.map((webinar, idx) => (
                  <WebinarCard key={webinar.id} item={webinar} type="webinar" animationDelay={idx * 0.03} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {activeCategory !== 'bundles' && !loading && filteredWebinars.length > 0 && renderPagination()}
        </div>
      </section>
    </div>
  );
}
